import React from "react";
import { useQuery } from "@tanstack/react-query";
import { History, FilePlus2, FileEdit, Trash2, Image as ImageIcon, ArrowRight, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const ACTION_META = {
  created: { label: "Creada", icon: FilePlus2, color: "text-[hsl(var(--status-completed))]" },
  updated: { label: "Modificada", icon: FileEdit, color: "text-[hsl(var(--accent))]" },
  status_changed: { label: "Cambio de estatus", icon: RefreshCw, color: "text-primary" },
  deleted: { label: "Eliminada", icon: Trash2, color: "text-destructive" },
  evidencia_added: { label: "Evidencia agregada", icon: ImageIcon, color: "text-[hsl(var(--status-pending))]" },
  evidencia_deleted: { label: "Evidencia eliminada", icon: ImageIcon, color: "text-destructive" },
};

const FIELD_LABELS = {
  cliente: "Cliente",
  solicitante: "Solicitante",
  fecha_arribo: "Fecha de Arribo",
  fecha_entrega: "Fecha de Entrega",
  puerto_entrada: "Puerto de Entrada",
  tipos_trabajo: "Tipos de Trabajo",
  observaciones: "Observaciones",
  estatus: "Estatus",
  partidas: "Partidas",
  items: "Partidas",
  po: "PO",
  sku: "SKU",
  vpn: "VPN",
  upc: "UPC",
  pedimento: "Pedimento",
  pz: "PZ",
  clasificacion_b: "Clas. B",
  clasificacion_c: "Clas. C",
  clasificacion_d: "Clas. D",
};

function fmtValue(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.length === 0 ? "—" : v.join(", ");
  return String(v);
}

function ChangeRow({ ch }) {
  if (ch.field === "partidas" && Array.isArray(ch.detail)) {
    return (
      <div className="text-xs space-y-1 ml-1 mt-1">
        {ch.detail.map((d, i) => (
          <div key={i} className="flex items-center gap-1.5 flex-wrap">
            <Badge variant="outline" className="text-[10px] font-mono">L{d.line}</Badge>
            <span className="text-muted-foreground">{FIELD_LABELS[d.field] || d.field}:</span>
            <span className="font-mono text-muted-foreground line-through">{fmtValue(d.from)}</span>
            <ArrowRight className="h-3 w-3 text-muted-foreground" />
            <span className="font-mono text-foreground">{fmtValue(d.to)}</span>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="text-xs flex items-center gap-1.5 flex-wrap ml-1 mt-1">
      <span className="text-muted-foreground">{FIELD_LABELS[ch.field] || ch.field}:</span>
      <span className="font-mono text-muted-foreground line-through">{fmtValue(ch.from)}</span>
      <ArrowRight className="h-3 w-3 text-muted-foreground" />
      <span className="font-mono text-foreground">{fmtValue(ch.to)}</span>
    </div>
  );
}

export default function HistoryTab({ otId }) {
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["history", otId],
    queryFn: async () => (await api.get(`/ots/${otId}/history`)).data,
  });

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Cargando historial...</div>;
  }
  if (logs.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center">
          <History className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Sin registros de cambios aún.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-0">
        <div className="relative">
          <div className="absolute left-7 top-0 bottom-0 w-px bg-border" />
          <ul className="space-y-0">
            {logs.map((log, i) => {
              const meta = ACTION_META[log.action] || { label: log.action, icon: History, color: "text-muted-foreground" };
              const Icon = meta.icon;
              return (
                <li key={log.id || i} className="relative flex gap-4 p-4 border-b border-border/60 last:border-b-0" data-testid={`history-entry-${i}`}>
                  <div className={`relative z-10 h-7 w-7 rounded-full bg-card border border-border flex items-center justify-center shrink-0 ${meta.color}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-semibold ${meta.color}`}>{meta.label}</span>
                      <span className="text-xs text-muted-foreground">por</span>
                      <span className="text-xs font-medium">{log.full_name || log.username}</span>
                      <span className="text-xs text-muted-foreground ml-auto font-mono">{(log.created_at || "").substring(0, 16).replace("T", " ")}</span>
                    </div>
                    {log.message && (
                      <p className="text-xs text-muted-foreground mt-1">{log.message}</p>
                    )}
                    {Array.isArray(log.changes) && log.changes.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {log.changes.map((ch, j) => <ChangeRow key={j} ch={ch} />)}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
